import { LightningAddressInfo } from "./types";

/**
 * Verify a Lightning Address by performing the first step of the LNURL-pay
 * protocol: GET https://<domain>/.well-known/lnurlp/<user>
 *
 * Returns parsed response or error info.
 */
export async function checkLightningAddress(
  address: string,
): Promise<LightningAddressInfo> {
  const parts = address.split("@");
  if (parts.length !== 2) {
    return {
      isValid: false,
      domain: "",
      user: "",
      error: "Invalid Lightning Address format — expected user@domain.",
    };
  }

  const [user, domain] = parts;

  if (!user || !domain || !domain.includes(".")) {
    return {
      isValid: false,
      domain: domain || "",
      user: user || "",
      error: "Invalid Lightning Address — the domain doesn't look right.",
    };
  }

  // Build the LNURL-pay well-known URL
  // Use HTTPS for clearnet, HTTP for .onion
  const protocol = domain.endsWith(".onion") ? "http" : "https";
  const url = `${protocol}://${domain}/.well-known/lnurlp/${encodeURIComponent(user)}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      return {
        isValid: false,
        domain,
        user,
        error: `Server responded with HTTP ${response.status}. The Lightning Address may not exist or the server is down.`,
      };
    }

    const data = await response.json();

    // Check for LNURL error response
    if (data.status === "ERROR") {
      return {
        isValid: false,
        domain,
        user,
        error: data.reason || "The server returned an error for this address.",
      };
    }

    // Validate required fields per LNURL-pay spec
    if (
      !data.callback ||
      data.minSendable == null ||
      data.maxSendable == null
    ) {
      return {
        isValid: false,
        domain,
        user,
        error:
          "The server responded but the data is incomplete — missing required LNURL-pay fields (callback, minSendable, maxSendable).",
      };
    }

    // Parse metadata for description and thumbnail
    let description: string | undefined;
    let thumbnail: string | undefined;

    if (data.metadata) {
      try {
        const metaArray = JSON.parse(data.metadata);
        if (Array.isArray(metaArray)) {
          for (const entry of metaArray) {
            if (!Array.isArray(entry) || entry.length < 2) continue;
            if (entry[0] === "text/plain") {
              description = entry[1];
            } else if (entry[0] === "image/png;base64") {
              thumbnail = `data:image/png;base64,${entry[1]}`;
            } else if (entry[0] === "image/jpeg;base64" && !thumbnail) {
              thumbnail = `data:image/jpeg;base64,${entry[1]}`;
            }
          }
        }
      } catch {
        // metadata wasn't valid JSON — not critical
      }
    }

    return {
      isValid: true,
      domain,
      user,
      callback: data.callback,
      minSendable: data.minSendable,
      maxSendable: data.maxSendable,
      metadata: data.metadata,
      commentAllowed: data.commentAllowed,
      description,
      thumbnail,
    };
  } catch (err) {
    // Network or CORS error
    return {
      isValid: false,
      domain,
      user,
      error:
        "Could not reach the Lightning Address server. This may be due to CORS restrictions, a network error, or the server being offline.",
    };
  }
}

/** Format millisatoshis to a human-readable sats string */
export function msatsToSatsDisplay(msat: number): string {
  const sats = Math.floor(msat / 1000);
  return sats.toLocaleString() + " sats";
}
